// Route stub created by tools/gen_web_routes.py from contract/catalog/screens.json; owned by hand from here on.
// About (A3U6, D191 (2), account-about): this build's one version beside the server's, both copied in one click for a
// bug report, and the links app-config publishes. It opens signed out too, standing alone. A19U5: What's new opens the
// newest changelog block, the open-source licences generated at build (scripts/about-data.mjs) show eight and then
// all, and outside production the version tapped seven times opens the developer tools, counting down from the fourth.
import { ids } from '@orca/api-client/testIds'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { type Column, DataTable } from '../../components/ui/data-table'
import { DescriptionList } from '../../components/ui/description-list'
import { Icon } from '../../components/ui/icons'
import { PageHeader } from '../../components/ui/page-header'
import { showSnackbar } from '../../components/ui/snackbar'
import licences from '../../generated/licences.json'
import whatsNew from '../../generated/whatsNew.json'
import { appConfigQuery } from '../../shared/appConfig/appConfig'
import '../../shared/appConfig/strings'
import { t } from '../../shared/i18n/i18n'
import { appVersion } from '../../shared/release/version'

/** The taps on the version that open the developer tools, and the one from which the rest are counted down. */
const TAPS = 7
const COUNTDOWN_FROM = 4
/** Licences shown before Show all. */
const FIRST_LICENCES = 8

type LicenceRow = { id: string; name: string; license: string }
const licenceRows: LicenceRow[] = licences.map((it) => ({ id: it.name, name: it.name, license: it.license }))

export const Route = createFileRoute('/account/about')({
  staticData: { screenId: 'AboutScreen', access: 'anyone' },
  component: AboutScreen,
})

function AboutScreen() {
  const { queryClient } = Route.useRouteContext()
  // Read at render, not at import: a test stubs the environment per case.
  const developerTools = import.meta.env.VITE_ORCA_ENV !== 'prod'
  const config = useQuery(appConfigQuery, queryClient)
  const links = config.data?.links
  const serverVersion = config.isPending
    ? t('core.loading')
    : (config.data?.server.version ?? t('appConfig.about.unknown'))
  const copy = () => {
    // The page may not be allowed the clipboard (an http origin, a denied permission); the versions stay on screen.
    navigator.clipboard
      ?.writeText(`Orca ${appVersion} · server ${serverVersion}`)
      .then(() => showSnackbar({ message: t('appConfig.about.copied'), tone: 'positive' }))
      .catch(() => {})
  }
  return (
    <main data-testid={ids.about.root} className="flex flex-col gap-(--stack-lg)">
      <PageHeader title={t('appConfig.about.title')} />
      <div className="grid items-start gap-(--stack-lg) xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-(--stack-lg)">
          <Card className="flex flex-col gap-(--stack-md) p-(--inset-lg)">
            <DescriptionList
              items={[
                {
                  term: t('appConfig.about.appVersion'),
                  value: developerTools ? <VersionTaps /> : appVersion,
                  testId: ids.about.appVersionValue,
                },
                {
                  term: t('appConfig.about.serverVersion'),
                  value: serverVersion,
                  testId: ids.about.serverVersionValue,
                },
              ]}
            />
            <Button variant="secondary" className="self-start" onClick={copy}>
              {t('appConfig.about.copy')}
            </Button>
          </Card>
          <ul className="flex flex-col">
            <WhatsNew />
            <AboutLink testId={ids.about.termsButton} href={links?.terms} label={t('appConfig.about.terms')} />
            <AboutLink testId={ids.about.privacyButton} href={links?.privacy} label={t('appConfig.about.privacy')} />
            <AboutLink testId={ids.about.supportButton} href={links?.support} label={t('appConfig.about.support')} />
          </ul>
        </div>
        <Licences />
      </div>
    </main>
  )
}

/** The version as a button: from the fourth tap a countdown, at the seventh the developer tools. */
function VersionTaps() {
  const navigate = useNavigate()
  const [taps, setTaps] = useState(0)
  const left = TAPS - taps
  const tap = () => {
    const next = taps + 1
    setTaps(next)
    if (next >= TAPS) {
      void navigate({ to: '/dev' })
      return
    }
    if (next >= COUNTDOWN_FROM) showSnackbar({ message: t('appConfig.about.tapsLeft', { count: TAPS - next }) })
  }
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={tap}
      data-taps-left={taps >= COUNTDOWN_FROM ? left : undefined}
      className="-ms-3.5 font-semibold text-fg"
    >
      {appVersion}
    </Button>
  )
}

/** A disclosure (`details`): the newest changelog block's lines, or a line saying there are none yet. */
function WhatsNew() {
  const [open, setOpen] = useState(false)
  return (
    <li>
      <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
        <summary
          data-testid={ids.about.whatsNewButton}
          className="press flex cursor-pointer list-none min-h-(--row-touch) pointer-fine:min-h-(--density-row) w-full items-center justify-between gap-3 rounded-(--radius-md) ps-3.5 pe-3 font-semibold text-[15px] text-fg hover:bg-hover"
        >
          {t('appConfig.about.whatsNew')}
          <span className="flex items-center gap-2">
            <span className="font-semibold text-caption text-fg-muted">{whatsNew.version ?? appVersion}</span>
            <Icon
              name="chevD"
              aria-hidden
              className={`size-(--icon-sm) shrink-0 text-fg-subtle ${open ? 'rotate-180' : ''}`}
            />
          </span>
        </summary>
        {open && (
          <ul
            id={ids.about.whatsNewList}
            data-testid={ids.about.whatsNewList}
            className="flex list-disc flex-col gap-1 ps-8 pe-3 pb-2 text-fg text-label"
          >
            {whatsNew.lines.length === 0 ? (
              <li className="list-none text-fg-muted">{t('appConfig.about.whatsNewNone')}</li>
            ) : (
              whatsNew.lines.map((line) => <li key={line}>{line}</li>)
            )}
          </ul>
        )}
      </details>
    </li>
  )
}

function Licences() {
  const [query, setQuery] = useState('')
  const [all, setAll] = useState(false)
  const found = query
    ? licenceRows.filter((it) => it.name.toLowerCase().includes(query.trim().toLowerCase()))
    : licenceRows
  const rows = all || query ? found : found.slice(0, FIRST_LICENCES)
  const columns: Column<LicenceRow>[] = [
    { key: 'name', label: t('appConfig.about.licencePackage') },
    { key: 'license', label: t('appConfig.about.licenceLicence'), type: 'tag', width: '140px' },
  ]
  return (
    <section aria-labelledby="about-licences" className="flex min-w-0 flex-col gap-(--stack-sm)">
      <h2 id="about-licences" className="font-bold text-caption text-fg-muted">
        {t('appConfig.about.licences')}
      </h2>
      <DataTable
        label={t('appConfig.about.licences')}
        columns={columns}
        rows={rows}
        search={{ value: query, onChange: setQuery }}
        pager={false}
        dense
        testId={ids.about.licencesList}
      />
      {!all && !query && (
        <div className="flex items-center justify-between gap-3 px-1">
          <span className="font-semibold text-caption text-fg-muted">
            {t('appConfig.about.licencesShown', { shown: rows.length, total: licenceRows.length })}
          </span>
          <Button variant="ghost" size="sm" data-testid={ids.about.licencesMoreButton} onClick={() => setAll(true)}>
            {t('appConfig.about.licencesAll', { total: licenceRows.length })}
          </Button>
        </div>
      )}
    </section>
  )
}

function AboutLink({ testId, href, label }: { testId: string; href: string | null | undefined; label: string }) {
  if (!href) return null
  return (
    <li>
      <a
        data-testid={testId}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-h-(--row-touch) pointer-fine:min-h-(--density-row) items-center justify-between gap-3 rounded-(--radius-md) ps-3.5 pe-3 font-semibold text-[15px] text-fg hover:bg-hover"
      >
        {label}
        <Icon name="chevR" aria-hidden className="size-(--icon-sm) shrink-0 text-fg-subtle" />
      </a>
    </li>
  )
}
